<template>
  <div class="scales-settings" v-loading="loading">

    <page-heading
      heading="Шкалы"
    >
      <div slot="right" class="el-row--flex">
        <library-scales-units-modal
          v-if="$canAndRulesPass(user, 'scales.edit_units')"
        >
          <el-button
            slot="reference"
            type="primary"
            size="small"
            icon="fas fa-pen"
          >
            Редактирование единиц измерения
          </el-button>
        </library-scales-units-modal>
      </div>
    </page-heading>

    <el-row :gutter="20" type="flex" class="scales-settings-row">
      <el-col :span="7">
        <el-card class="scales-panel" shadow="never">
        <div class="scales-groups-header">
          <span class="scales-groups-header-title">Список шкал</span>
          <el-tooltip
            v-if="$canAndRulesPass(user, $permissions.CONFIGURABLE_SCALES.CREATE)"
            content="Добавить группу"
            placement="top"
            :open-delay="500"
          >
            <el-button
              class="scales-group-add-button"
              size="mini"
              circle
              icon="fas fa-plus"
              @click="createGroup"
            ></el-button>
          </el-tooltip>
        </div>

        <div class="scales-groups-list">
          <div
            v-for="group in displayGroups"
            :key="group.group.id"
            class="scales-group"
          >
            <div
              class="scales-group-row"
              :class="{'scales-group-row--menu-open': openMenuGroupId === group.group.id}"
              @click="toggleGroup(group.group.id)"
            >
              <i
                class="scales-group-chevron fas"
                :class="isExpanded(group.group.id) ? 'fa-chevron-down' : 'fa-chevron-right'"
              ></i>

              <click-to-edit
                v-if="$canAndRulesPass(group.group, $permissions.CONFIGURABLE_SCALES.UPDATE)"
                :ref="'groupName' + group.group.id"
                class="scales-group-name"
                v-model="group.group.name"
                @input="renameGroup(group.group)"
                @click.native.stop
              ></click-to-edit>
              <span v-else class="scales-group-name">{{ group.group.name }}</span>

              <span class="scales-group-actions" @click.stop>
                <el-tooltip
                  v-if="$canAndRulesPass(user, $permissions.CONFIGURABLE_SCALES.CREATE)"
                  content="Добавить шкалу"
                  placement="top"
                  :open-delay="500"
                >
                  <el-button
                    class="table-action-button"
                    round
                    size="mini"
                    icon="fas fa-plus"
                    @click="createScale(group.group)"
                  ></el-button>
                </el-tooltip>
                <el-dropdown
                  v-if="$canAndRulesPass(group.group, $permissions.CONFIGURABLE_SCALES.UPDATE) || $canAndRulesPass(group.group, $permissions.CONFIGURABLE_SCALES.DELETE)"
                  trigger="click"
                  @command="onGroupCommand(group, $event)"
                  @visible-change="openMenuGroupId = $event ? group.group.id : null"
                >
                  <el-button
                    class="table-action-button"
                    round
                    size="mini"
                    icon="fas fa-ellipsis-v"
                  ></el-button>
                  <el-dropdown-menu slot="dropdown">
                    <el-dropdown-item v-if="$canAndRulesPass(group.group, $permissions.CONFIGURABLE_SCALES.UPDATE)" command="rename" icon="fas fa-pen">
                      Переименовать
                    </el-dropdown-item>
                    <el-dropdown-item
                      v-if="$canAndRulesPass(group.group, $permissions.CONFIGURABLE_SCALES.UPDATE)"
                      command="enable"
                      icon="fas fa-check"
                      :disabled="!group.scales.some(scale => scale.disabled)"
                    >
                      Активировать все шкалы
                    </el-dropdown-item>
                    <el-dropdown-item
                      v-if="$canAndRulesPass(group.group, $permissions.CONFIGURABLE_SCALES.UPDATE)"
                      command="disable"
                      icon="fas fa-ban"
                      :disabled="!group.scales.some(scale => !scale.disabled)"
                    >
                      Деактивировать все шкалы
                    </el-dropdown-item>
                    <el-dropdown-item v-if="$canAndRulesPass(group.group, $permissions.CONFIGURABLE_SCALES.DELETE)" command="delete" icon="far fa-trash-alt" divided>
                      Удалить группу
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
              </span>
            </div>

            <div v-show="isExpanded(group.group.id)" class="scales-group-scales">
              <div
                v-for="scale in group.scales"
                :key="scale.id"
                class="scales-group-scale"
                :class="{active: selectedScale && selectedScale.id === scale.id}"
                @click="selectScale(scale)"
              >
                {{ scale.name || 'Без названия' }}
                <el-tag v-if="scale.disabled" type="info" size="mini">Отключена</el-tag>
              </div>
            </div>
          </div>

          <div v-if="!displayGroups.length" class="scales-groups-empty">Нет групп</div>
        </div>
        </el-card>
      </el-col>

      <el-col :span="17">
        <el-card class="scales-panel scales-panel--sticky" shadow="never">
          <ScaleTemplate
            v-if="selectedScale"
            :key="selectedScale.id"
            :template="selectedScale"
            :groups="groupsList"
            @update:template="onScaleUpdated"
            @template-deleted="onScaleDeleted"
          ></ScaleTemplate>
          <div v-else class="scales-settings-placeholder">Шкала не выбрана</div>
        </el-card>
      </el-col>
    </el-row>

  </div>
</template>

<script>
import requestSender from "@/api/base/requestSender";
import ClickToEdit from "@/components/ClickToEdit.vue";
import PageHeading from "@/components/PageHeading.vue";
import LibraryScalesUnitsModal from "@/components/library/LibraryScalesUnitsModal.vue";
import {mapGetters} from "vuex";
import {Notification} from "element-ui";
import ScaleTemplate from "@/components/configurableScales/settings/ScaleTemplate.vue";
import {resetScaleGroupsCache} from "@/components/configurableScales/ScaleGroupSelect.vue";

export default {
  name: "scales-settings",
  components: {ScaleTemplate, LibraryScalesUnitsModal, PageHeading, ClickToEdit},
  props: {},
  watch: {},
  computed: {
    ...mapGetters(['user']),
    // Плоский список групп для селекта в ScaleTemplate — дерево уже содержит их все
    groupsList() {
      return this.groups.map(group => group.group);
    },
    displayGroups() {
      return this.sortPinnedFirst(this.groups, this.pinnedGroupIds, group => group.group.id)
        .map(group => Object.assign({}, group, {
          scales: this.sortPinnedFirst(group.scales, this.pinnedScaleIds, scale => scale.id),
        }));
    },
  },
  data() {
    return {
      loading: false,
      groups: [],
      selectedScale: null,
      collapsedGroupIds: {},
      openMenuGroupId: null,
      pinnedGroupIds: [],
      pinnedScaleIds: [],
    }
  },
  mounted() {
    this.loadTree();
  },
  methods: {
    loadTree(selectScaleId = null) {
      this.loading = true;
      // Справочник тут правят — общий кэш ScaleGroupSelect устарел
      resetScaleGroupsCache();
      return requestSender('get', 'scale/grouped-templates-list', {
        sort: 'name',
        with_disabled: 1,
        expand: 'permissions,used_in_cards',
      })
        .then(data => {
          this.groups = data.groups;
          this.selectScaleById(selectScaleId || (this.selectedScale ? this.selectedScale.id : null));
        })
        .finally(() => {
          this.loading = false;
        })
    },

    sortPinnedFirst(items, pinnedIds, getId) {
      const rank = item => {
        const index = pinnedIds.indexOf(getId(item));
        return index === -1 ? Infinity : index;
      };
      return items.slice().sort((a, b) => rank(a) - rank(b));
    },

    selectScaleById(id) {
      if (!id) {
        this.selectedScale = null;
        return;
      }
      for (const group of this.groups) {
        const scale = group.scales.find(s => s.id === id);
        if (scale) {
          this.selectedScale = scale;
          this.$set(this.collapsedGroupIds, group.group.id, false);
          return;
        }
      }
      this.selectedScale = null;
    },

    selectScale(scale) {
      this.selectedScale = scale;
    },

    isExpanded(groupId) {
      return !this.collapsedGroupIds[groupId];
    },

    toggleGroup(groupId) {
      this.$set(this.collapsedGroupIds, groupId, !this.collapsedGroupIds[groupId]);
    },

    onGroupCommand(group, command) {
      switch (command) {
        case 'rename':
          this.startRenameGroup(group.group);
          break;
        case 'enable':
        case 'disable':
          this.setGroupScalesDisabled(group, command === 'disable');
          break;
        case 'delete':
          this.confirmDeleteGroup(group);
          break;
      }
    },
    setGroupScalesDisabled(group, disabled) {
      const action = disabled ? 'Деактивировать' : 'Активировать';
      this.$confirm(`${action} все шкалы группы «${group.group.name}» (${group.scales.length} шт.)?`, 'Внимание', {
        confirmButtonText: 'Да',
        cancelButtonText: 'нет',
        type: 'warning'
      }).then(() => {
        this.loading = true;
        requestSender('post', 'scale/group-set-disabled', {id: group.group.id, disabled})
          .then(() => this.loadTree())
          .finally(() => {
            this.loading = false;
          });
      }).catch(() => {}); // отмена диалога
    },

    confirmDeleteGroup(group) {
      if (group.scales.length) {
        this.warnGroupNotEmpty();
        return;
      }
      this.$confirm('Вы уверены, что хотите удалить группу шкал?', 'Внимание', {
        confirmButtonText: 'Да, удалить',
        cancelButtonText: 'нет',
        type: 'warning'
      }).then(() => {
        this.deleteGroup(group.group);
      }).catch(() => {}); // отмена диалога
    },

    startRenameGroup(group) {
      // ref внутри v-for Vue всегда регистрирует массивом, даже когда имя уникально на группу
      const [editor] = [].concat(this.$refs['groupName' + group.id] || []);
      if (editor) {
        editor.startEdit();
      }
    },

    createGroup() {
      this.loading = true;
      requestSender('post', 'scale/group-create', {})
        .then(data => {
          this.pinnedGroupIds.unshift(data.group.id);
          return this.loadTree();
        })
        .finally(() => {
          this.loading = false;
        })
    },

    renameGroup(group) {
      if (group.name) {
        this.unpin(this.pinnedGroupIds, group.id);
      }
      requestSender('post', 'scale/group-update', {id: group.id, name: group.name})
        .then(() => this.loadTree())
        .catch(() => this.loadTree()); // откатываем локальную правку названия, если сервер отклонил (например, пустое имя)
    },

    // Текст держим таким же, как в ConfigurableScaleController::actionGroupDelete,
    // и показываем тем же Notification.error, что и ошибки с бэка — вид уведомления не меняется
    warnGroupNotEmpty() {
      Notification.error({
        title: 'Ошибка',
        message: 'Группу нельзя удалить, так как в ней есть шкалы. Сначала удалите или переместите шкалы из группы',
      });
    },

    deleteGroup(group) {
      this.unpin(this.pinnedGroupIds, group.id);
      requestSender('post', 'scale/group-delete', {id: group.id})
        .then(() => this.loadTree())
        .catch(() => {}); // ошибка (например "в группе есть шкалы") уже показана глобальным обработчиком requestSender
    },

    createScale(group) {
      if (!group) {
        return;
      }
      this.loading = true;
      requestSender('post', 'scale/create', {scale_group_id: group.id})
        .then(data => {
          this.pinnedScaleIds.unshift(data.scale.id);
          return this.loadTree(data.scale.id);
        })
        .finally(() => {
          this.loading = false;
        })
    },

    unpin(pinnedIds, id) {
      const index = pinnedIds.indexOf(id);
      if (index !== -1) {
        pinnedIds.splice(index, 1);
      }
    },

    onScaleUpdated(scale, rebuildTree) {
      if (scale.name) {
        this.unpin(this.pinnedScaleIds, scale.id);
      }

      if (rebuildTree) {
        this.loadTree(scale.id);
        return;
      }

      // selectedScale — ссылка на объект внутри groups, поэтому обновление на месте
      // сразу отражается и в списке слева
      if (this.selectedScale) {
        Object.keys(scale).forEach(key => {
          this.$set(this.selectedScale, key, scale[key]);
        });
      }
    },

    onScaleDeleted(scale) {
      if (scale) {
        this.unpin(this.pinnedScaleIds, scale.id);
      }
      this.loadTree();
    },
  }
}
</script>


<style lang="scss">
.el-main:has(.scales-settings) {
  overflow: visible;
}
</style>

<style lang="scss" scoped>
@import "@/assets/css/project-variables.scss";
@import "@/components/configurableScales/scalePills.scss";

.scales-settings-row {
  align-items: stretch;
}

.scales-panel {
  height: 100%;

  ::v-deep .el-card__body {
    padding: 20px;
  }
}

.scales-panel--sticky {
  position: sticky;
  top: 20px;
  height: auto;
  box-sizing: border-box;
  max-height: calc(100vh - 40px);
  display: flex;
  flex-direction: column;

  ::v-deep .el-card__body {
    overflow: auto;
  }
}

.scales-settings-placeholder {
  color: #909399;
  text-align: center;
  padding-top: 60px;
  font-size: 16px;
}

.scales-groups-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.scales-groups-header-title {
  font-weight: 500;
  font-size: 20px;
}

// Кнопка создания группы — акцентная, в отличие от полупрозрачных действий внутри строк
.scales-group-add-button.el-button {
  color: $--color-primary;
  border: 1px solid $scale-pill-primary-light-7;
  background-color: $scale-pill-primary-light-9;
  padding: 6px;

  &:hover {
    color: #ffffff;
    background-color: $--color-primary;
    border-color: $--color-primary;
  }
}

.scales-groups-empty {
  color: #909399;
  text-align: center;
  padding: 20px 0;
}

.scales-group {
  margin-bottom: 12px;
}

.scales-group-row {
  @include scale-pill($scale-pill-border-strong);

  display: flex;
  align-items: center;
  gap: 8px;

  &:hover .scales-group-actions,
  &.scales-group-row--menu-open .scales-group-actions {
    opacity: 1;
  }
}

.scales-group-chevron {
  width: 12px;
  font-size: 12px;
  color: #909399;
  flex-shrink: 0;
}

.scales-group-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  // Название группы редактируется по клику, но пунктир из newDesign здесь лишний —
  // строка уже выглядит как кликабельная «таблетка»
  ::v-deep .click-to-edit-enabled {
    border: 0 !important;
    background-color: transparent;
    box-shadow: none;
    padding-left: 0;
    padding-right: 0;
  }
}

// Действия проявляются на ховере строки, чтобы список оставался спокойным
.scales-group-actions {
  display: flex;
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.2s;
}

.scales-group-scales {
  margin-top: 8px;
  padding-left: 20px;
}

.scales-group-scale {
  @include scale-pill;

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;

  &.active {
    @include scale-pill-active;
  }
}
</style>
